<template>
    <div class="checkout__form-col">
        <h3 class="h4">Contact Details</h3>
        <div class="row">
            <!-- First & Last Name -->
            <div v-for="field in ['firstName','lastName']" :key="field" class="col-6 form-group position-relative">
                <label class="form-label">
                    {{ field === 'firstName' ? 'First Name' : 'Last Name' }} *
                </label>
                <input
                    v-model="form[field]"
                    :name="`user[${field === 'firstName' ? 'first_name' : 'last_name'}]`"
                    type="text"
                    class="form-control"
                    :class="{ 'is-invalid': touched[field] && errors[field] }"
                    @blur="touched[field] = true"
                />
                <div v-if="touched[field] && errors[field]" class="invalid-feedback position-absolute">
                    {{ errors[field] }}
                </div>
            </div>

            <!-- Email -->
            <div class="col-12 form-group position-relative">
                <label class="form-label">Email *</label>
                <input
                    v-model="form.email"
                    name="user[email]"
                    type="email"
                    class="form-control"
                    :class="{ 'is-invalid': touched.email && errors.email }"
                    @blur="touched.email = true"
                />
                <div v-if="touched.email && errors.email" class="invalid-feedback position-absolute">
                    {{ errors.email }}
                </div>
            </div>

            <!-- Phone -->
            <div class="col-12 form-group position-relative">
                <label class="form-label">Phone *</label>
                <input
                    v-model="form.phone"
                    name="user[phone]"
                    type="text"
                    class="form-control"
                    :class="{ 'is-invalid': touched.phone && errors.phone }"
                    @blur="touched.phone = true"
                />
                <div v-if="touched.phone && errors.phone" class="invalid-feedback position-absolute">
                    {{ errors.phone }}
                </div>
            </div>

            <!-- Dispatch Options -->
            <div class="col-12">
                <!-- Only show toggle if both options are enabled -->
                <div v-if="deliveryEnabled && clickCollectEnabled" :class="['toggle-switch mt-3 mb-3', { 'is-invalid': touched.dispatch && errors.dispatch }]">

                    <input
                        type="radio"
                        id="pickup"
                        name="user[dispatch]"
                        class="btn-check"
                        v-model="form.dispatch"
                        value="pickup"
                        @change="selectPickupDispatch"
                    />
                    <label for="pickup">{{ pickupLabel }}</label>

                    <input
                        type="radio"
                        id="delivery"
                        name="user[dispatch]"
                        class="btn-check"
                        v-model="form.dispatch"
                        value="delivery"
                        :disabled="!deliveryEnabled"
                        @change="selectDeliveryDispatch"
                    />
                    <label for="delivery" :class="{ disabled: !deliveryEnabled }">{{ deliveryLabel }}</label>

                    <div class="toggle-slider"></div>
                </div>
                <!-- If only one option is enabled, show it as selected by default -->
                <div v-else>
                    <div v-if="clickCollectEnabled" class="mt-2 mb-3">
                        <h5 class="dispatch-widget__output__heading">{{ pickupHeading }}</h5>
                        <div class="message-box">
                            <div class="message-box__label">
                                <h4>{{ storeName }}</h4>
                                <p v-if="storeAddress" v-html="storeAddress.replace(/\n/g, '<br>')"></p>
                            </div>
                        </div>
                        <div v-if="pickupNote" class="pickup-note" v-html="pickupNote"></div>
                    </div>
                </div>
                <!-- Pickup Information (when both options available and pickup selected) -->
                <div v-if="deliveryEnabled && clickCollectEnabled && form.dispatch === 'pickup'" class="mt-2 mb-3">
                    <h5 class="dispatch-widget__output__heading">{{ pickupHeading }}</h5>
                    <div class="message-box">
                        <div class="message-box__label">
                            <h4>{{ storeName }}</h4>
                            <p v-if="storeAddress" v-html="storeAddress.replace(/\n/g, '<br>')"></p>
                        </div>
                    </div>
                    <div v-if="pickupNote" class="pickup-note" v-html="pickupNote"></div>
                </div>

                <div v-if="touched.dispatch && errors.dispatch" class="invalid-feedback d-block position-absolute">
                    {{ errors.dispatch }}
                </div>
            </div>

            <!-- Delivery Options Selection -->
            <div v-if="form.dispatch === 'delivery' && availableDeliveryOptions.length > 0" class="mt-3">
                <h5>Select Delivery Option:</h5>
                <div class="delivery-options">
                    <label
                        v-for="option in availableDeliveryOptions"
                        :key="option.id"
                        class="delivery-option"
                        :class="{
                            'active': selectedDeliveryOption && selectedDeliveryOption.id === option.id,
                            'disabled': option.meets_min_order === false
                        }"
                    >
                        <div class="delivery-option-content">
                            <div class="delivery-option-left">
                                <input
                                    type="radio"
                                    :value="option"
                                    :checked="selectedDeliveryOption && selectedDeliveryOption.id === option.id"
                                    @change="selectDeliveryOption(option)"
                                    name="deliveryOption"
                                    class="delivery-option-radio"
                                    :disabled="option.meets_min_order === false"
                                />
                                <div class="delivery-option-details">
                                    <div class="delivery-option-title">{{ option.title || 'Delivery Option' }}</div>
                                    <div class="delivery-option-subtext small" v-if="option.max_distance">
                                        <small>Delivery within {{ option.max_distance }}km</small>
                                    </div>
                                    <div class="delivery-option-subtext small" v-else-if="option.zone_match === 'single' && option.matched_zones">
                                        <small>Zone: {{ option.matched_zones[0] }}</small>
                                    </div>
                                    <div class="delivery-option-subtext small" v-else-if="option.zone_match === 'intersection'">
                                        <small>Coverage area delivery</small>
                                    </div>
                                    <div class="delivery-option-subtext small" v-else-if="option.free_shipping_applied">
                                        <small>Your order qualifies for free delivery</small>
                                    </div>
                                    <div v-if="option.meets_min_order === false" class="text-danger small">
                                        <small>Spend ${{ option.amount_needed.toFixed(2) }} more to qualify</small>
                                    </div>
                                </div>
                            </div>
                            <div class="delivery-option-price">${{ parseFloat(option.price).toFixed(2) }}</div>
                        </div>
                    </label>
                </div>
            </div>

            <!-- Warning when options exist but none meet minimum order -->
            <div v-if="form.dispatch === 'delivery' && availableDeliveryOptions.length > 0 && availableDeliveryOptions.every(opt => opt.meets_min_order === false)" class="alert alert-warning mt-3">
                <strong>Minimum Order Not Met:</strong>
                <small>Your cart total doesn't meet the minimum order requirements for delivery. Please add more items or select pickup.</small>
            </div>

            <!-- Address Fields - Only show for delivery -->
            <template v-if="form.dispatch === 'delivery'">
                <!-- Address -->
                <div class="col-12 form-group position-relative">
                    <label class="form-label">Address *</label>
                    <input
                        ref="addressAutocompleteInput"
                        v-model="form.address"
                        name="user[address]"
                        type="text"
                        class="form-control"
                        :class="{ 'is-invalid': touched.address && errors.address }"
                        @blur="touched.address = true"
                    />
                    <div v-if="touched.address && errors.address" class="invalid-feedback position-absolute">
                        {{ errors.address }}
                    </div>
                </div>

                <!-- Suburb, State, Postcode -->
                <div v-for="field in ['suburb','state','postcode']" :key="field" class="col-4 form-group position-relative">
                    <label class="form-label">{{ field.charAt(0).toUpperCase() + field.slice(1) }} *</label>
                    <input
                        v-model="form[field]"
                        :name="`user[${field}]`"
                        type="text"
                        class="form-control"
                        :class="{ 'is-invalid': touched[field] && errors[field] }"
                        @blur="touched[field] = true"
                    />
                    <div v-if="touched[field] && errors[field]" class="invalid-feedback position-absolute">
                        {{ errors[field] }}
                    </div>
                </div>
            </template>

            <!-- Error when no delivery options available for address -->
            <div v-if="showDeliveryUnavailable" class="mt-3">
                <div class="delivery-options">
                    <div class="delivery-option disabled">
                        <div class="delivery-option-content">
                            <div class="delivery-option-left">
                                <div class="delivery-option-details">
                                    <div class="delivery-option-title">Delivery Not Available</div>
                                    <div class="delivery-option-subtext small">
                                        <small>{{ deliveryUnavailableMessage }}</small>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div class="col-12 form-group">
                <button
                    type="button"
                    :disabled="!isFormValid || cartItems.length === 0 || isUpdatingCart || isFetchingDeliveryOptions || (form.dispatch === 'delivery' && (selectedDeliveryOption === null || selectedDeliveryOption?.id === 'pickup')) || isProcessing"
                    class="btn btn-lg btn-block btn-rounded with-loader"
                    @click="handleSubmit"
                >
                    <!-- always show this text -->
                    <span class="btn-label">
                        {{ isProcessing ? 'Processing...' : 'Proceed to Payment' }}
                        <svg v-if="!isProcessing" focusable="false" role="img" class="icon-arrow">
                          <use xlink:href="/build/assets/icons.svg#icon--arrow-right"/>
                        </svg>
                      </span>

                    <!-- overlay spinner when loading -->
                    <div v-if="isCheckingAvailability || isUpdatingCart || isFetchingDeliveryOptions || isProcessing" class="loader-overlay">
                        <div class="loader"></div>
                    </div>
                </button>
            </div>
        </div>
    </div>
</template>

<script setup>
import {ref, reactive, computed, watch, onMounted, onUnmounted, nextTick} from 'vue'
import {useCart} from '@/frontend/vue/composables/useCart'
import {useDeliveryConfig} from '@/frontend/vue/composables/useDeliveryConfig'
// import {trackAddShippingInfo} from '@/frontend/vue/composables/useGTMTracking' // Commented out - may be added later
import axios from 'axios'
import debounce from 'lodash/debounce'

// --- state & props ---
const {cartItems, loadCart, selectedDeliveryOption, setDeliveryOption, itemsTotal, isUpdatingCart, saveCart, discountAmount} = useCart()
const {deliveryConfig, getDeliveryConfig} = useDeliveryConfig()
const isFetchingDeliveryOptions = ref(false)
const isCheckingAvailability = ref(false)
const availabilityResults = ref({})
const availableDeliveryOptions = ref([])
const deliveryEnabled = ref(false)
const clickCollectEnabled = ref(false)
const isProcessing = ref(false)
const storeName = ref('Store')
const storeAddress = ref('')
const pickupNote = ref('')
const deliveryOptionsValidatedAddressKey = ref('')
const addressAutocompleteInput = ref(null)
const isAddressAutocompleteInitialised = ref(false)
let placeAutocompleteElement = null
let placeAutocompleteListener = null
let googleMapsLoadInterval = null
let googleMapsLoadTimeout = null

// Custom text labels from backend
const form_element = document.querySelector('form[data-vue="checkout-view"]')
const deliveryLabel = form_element?.getAttribute('data-delivery-label') || 'Delivery'
const pickupLabel = form_element?.getAttribute('data-pickup-label') || 'Pickup'
const deliveryHeading = form_element?.getAttribute('data-delivery-heading') || 'Enter your address to check delivery availability:'
const pickupHeading = form_element?.getAttribute('data-pickup-heading') || 'Nearest Centre with Stock:'

// Handle form submission
const handleSubmit = async () => {
    if (!isFormValid.value || cartItems.value.length === 0 || isUpdatingCart.value || isFetchingDeliveryOptions.value || (form.dispatch === 'delivery' && (selectedDeliveryOption.value === null || selectedDeliveryOption.value?.id === 'pickup'))) {
        return;
    }

    isProcessing.value = true;

    try {
        // Prepare user data from the form
        const userData = {
            first_name: form.firstName,
            last_name: form.lastName,
            email: form.email,
            phone: form.phone,
            address: form.address,
            suburb: form.suburb,
            state: form.state,
            postcode: form.postcode,
            dispatch: form.dispatch
        };

        await saveCart(() => {
            const checkout_form = document.querySelector('form[data-vue="checkout-view"]');
            if (checkout_form) {
                checkout_form.submit();
            } else {
                console.error('Checkout form not found');
                isProcessing.value = false;
            }
        }, userData);
    } catch (error) {
        console.error('Error updating cart before checkout:', error);
        isProcessing.value = false;
    }
}

// Load persisted lookups once
const lookups = ref(JSON.parse(localStorage.getItem('ec_addr_lookup') || '{}'))

const props = defineProps({
    defaults: {type: Object, default: () => ({})}
})

const form = reactive({
    firstName: props.defaults.firstName || '',
    lastName: props.defaults.lastName || '',
    email: props.defaults.email || '',
    phone: props.defaults.phone || '',
    dispatch: 'pickup',
    address: '',
    suburb: '',
    state: '',
    postcode: ''
})

const touched = reactive({
    firstName: false, lastName: false, email: false, phone: false,
    dispatch: false, address: false, suburb: false, state: false, postcode: false
})

const buttonStyle = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
}

// validators
const validators = {
    firstName: v => v.trim() ? '' : 'required',
    lastName: v => v.trim() ? '' : 'required',
    email: v => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v) ? '' : 'Invalid email',
    phone: v => v.trim() ? '' : 'required',
    dispatch: v => v ? '' : 'please select',
    address: v => form.dispatch === 'delivery' ? (v.trim() ? '' : 'required') : '',
    suburb: v => form.dispatch === 'delivery' ? (v.trim() ? '' : 'required') : '',
    state: v => form.dispatch === 'delivery' ? (v.trim() ? '' : 'required') : '',
    postcode: v => form.dispatch === 'delivery' ? (v.trim() ? '' : 'required') : ''
}

const errors = computed(() =>
    Object.fromEntries(Object.entries(validators).map(([k, fn]) => [k, fn(form[k])]))
)

const isFormValid = computed(() =>
    Object.values(errors.value).every(msg => msg === '') &&
    !isCheckingAvailability.value
)

const showDeliveryUnavailable = computed(() =>
    form.dispatch === 'delivery'
    && hasCompleteDeliveryAddress()
    && deliveryOptionsValidatedAddressKey.value === deliveryAddressKey()
    && availableDeliveryOptions.value.length === 0
    && !isFetchingDeliveryOptions.value
    && !isCheckingAvailability.value
)

const deliveryUnavailableMessage = computed(() => {
    if (clickCollectEnabled.value) {
        return `Delivery isn’t available for this address. Please choose ${pickupLabel.toLowerCase()} to continue.`
    }

    return 'Delivery isn’t available for this address.'
})

// --- persistence helpers ---
const updateSessionStorage = debounce(() => {
    sessionStorage.setItem('ec_checkout_addr',
        JSON.stringify({
            address: form.address,
            suburb: form.suburb,
            state: form.state,
            postcode: form.postcode,
            dispatch: form.dispatch
        })
    )
}, 300)

const cleanupAddressAutocomplete = () => {
    if (placeAutocompleteListener?.remove) {
        placeAutocompleteListener.remove()
    }

    placeAutocompleteListener = null
    placeAutocompleteElement = null
    isAddressAutocompleteInitialised.value = false
}

const parsePlaceAddress = (place) => {
    const details = {
        address: '',
        suburb: '',
        state: '',
        postcode: '',
    }

    if (!place?.address_components) {
        details.address = place?.formatted_address || addressAutocompleteInput.value?.value || form.address
        return details
    }

    place.address_components.forEach((component) => {
        const type = component.types[0]

        if (type === 'street_number') {
            details.address = component.long_name
        }

        if (type === 'route') {
            details.address += `${details.address ? ' ' : ''}${component.long_name}`
        }

        if (type === 'locality') {
            details.suburb = component.long_name
        }

        if (type === 'administrative_area_level_1') {
            details.state = component.short_name
        }

        if (type === 'postal_code') {
            details.postcode = component.long_name
        }
    })

    if (!details.address && place?.formatted_address) {
        details.address = place.formatted_address
    }

    return details
}

const handlePlaceSelect = async () => {
    const place = placeAutocompleteElement?.getPlace()
    const details = parsePlaceAddress(place)

    if (details.address) {
        form.address = details.address
    }

    if (details.suburb) {
        form.suburb = details.suburb
    }

    if (details.state) {
        form.state = details.state
    }

    if (details.postcode) {
        form.postcode = details.postcode
    }

    touched.address = true
    touched.suburb = true
    touched.state = true
    touched.postcode = true

    updateSessionStorage.flush?.()

    await nextTick()
    if (form.dispatch === 'delivery' && hasCompleteDeliveryAddress()) {
        checkAvailability()
    }
}

const initAddressAutocomplete = async () => {
    if (form.dispatch !== 'delivery') {
        return false
    }

    await nextTick()

    if (!window.google?.maps?.places?.Autocomplete || !addressAutocompleteInput.value) {
        return false
    }

    if (isAddressAutocompleteInitialised.value && placeAutocompleteElement) {
        return true
    }

    cleanupAddressAutocomplete()

    placeAutocompleteElement = new window.google.maps.places.Autocomplete(
        addressAutocompleteInput.value,
        {componentRestrictions: {country: 'au'}, types: ['address']}
    )

    placeAutocompleteListener = placeAutocompleteElement.addListener('place_changed', handlePlaceSelect)
    isAddressAutocompleteInitialised.value = true

    return true
}

const stopWaitingForGoogleMaps = () => {
    if (googleMapsLoadInterval) {
        clearInterval(googleMapsLoadInterval)
        googleMapsLoadInterval = null
    }

    if (googleMapsLoadTimeout) {
        clearTimeout(googleMapsLoadTimeout)
        googleMapsLoadTimeout = null
    }
}

const waitForGoogleMaps = async () => {
    if (await initAddressAutocomplete()) {
        return
    }

    stopWaitingForGoogleMaps()

    googleMapsLoadInterval = setInterval(async () => {
        if (await initAddressAutocomplete()) {
            stopWaitingForGoogleMaps()
        }
    }, 100)

    googleMapsLoadTimeout = setTimeout(stopWaitingForGoogleMaps, 10000)
}

const saveLookups = (newEntries) => {
    lookups.value = {...lookups.value, ...newEntries}
    localStorage.setItem('ec_addr_lookup', JSON.stringify(lookups.value))
    window.dispatchEvent(new Event('ec:availability-updated'))
}

const setPickup = () => {
    form.dispatch = 'pickup';
    setDeliveryOption({ id: 'pickup', price: 0 });
}

const hasCompleteDeliveryAddress = () => {
    return [form.address, form.suburb, form.state, form.postcode]
        .every(value => String(value || '').trim() !== '')
}

const deliveryAddressKey = () => {
    return [form.address, form.suburb, form.state, form.postcode]
        .map(value => String(value || '').trim().toLowerCase())
        .join('|')
}

const validDeliveryOptions = (options = availableDeliveryOptions.value) => {
    return (Array.isArray(options) ? options : [])
        .filter(option => option && option.meets_min_order !== false)
}

const findMatchingDeliveryOption = (selected, options = availableDeliveryOptions.value) => {
    if (!selected || selected.id === 'pickup') {
        return null
    }

    return validDeliveryOptions(options).find(option => option.id === selected.id) || null
}

const cheapestValidDeliveryOption = (options = availableDeliveryOptions.value) => {
    return [...validDeliveryOptions(options)]
        .sort((a, b) => parseFloat(a.price || 0) - parseFloat(b.price || 0))[0] || null
}

const syncDispatchWithAvailableMethods = () => {
    if (deliveryEnabled.value && !clickCollectEnabled.value) {
        form.dispatch = 'delivery'
        return
    }

    if (!deliveryEnabled.value && clickCollectEnabled.value) {
        form.dispatch = 'pickup'
        return
    }

    if (!deliveryEnabled.value && !clickCollectEnabled.value) {
        form.dispatch = ''
        return
    }

    if (!form.dispatch) {
        form.dispatch = 'pickup'
    }
}

const syncSelectedDeliveryOption = (options = availableDeliveryOptions.value) => {
    if (form.dispatch === 'pickup') {
        setDeliveryOption({ id: 'pickup', price: 0 })
        return
    }

    if (form.dispatch !== 'delivery' || !deliveryEnabled.value) {
        setDeliveryOption(null)
        return
    }

    if (!hasCompleteDeliveryAddress()) {
        setDeliveryOption(null)
        return
    }

    const matchingOption = findMatchingDeliveryOption(selectedDeliveryOption.value, options)
    if (matchingOption) {
        setDeliveryOption(matchingOption)
        return
    }

    setDeliveryOption(cheapestValidDeliveryOption(options))
}

const selectPickupDispatch = () => {
    touched.dispatch = true
    setPickup()
}

const selectDeliveryDispatch = () => {
    touched.dispatch = true
    availableDeliveryOptions.value = []
    deliveryOptionsValidatedAddressKey.value = ''
    setDeliveryOption(null)

    if (!hasCompleteDeliveryAddress()) {
        touched.address = true
        touched.suburb = true
        touched.state = true
        touched.postcode = true
        return
    }

    fetchDeliveryOptions()
}

// Wrapper for setDeliveryOption with GTM tracking
const selectDeliveryOption = (option) => {
    setDeliveryOption(option);

    // Track shipping info when delivery option is selected (not pickup)
    if (option && option.id !== 'pickup') {
        const shippingTier = option.title || 'Standard Delivery';
        // GTM tracking commented out - may be added later
        // const cartItemsArray = cartItems.value.map(item => ({
        //     product_id: item.product_id || item.id,
        //     product_name: item.product_name || item.name,
        //     variant_name: item.variant_name || item.variant,
        //     price: item.price,
        //     quantity: item.quantity,
        // }));
        // trackAddShippingInfo(cartItemsArray, itemsTotal.value, shippingTier);
    }
}

async function checkAvailabilityImpl() {
    if (!hasCompleteDeliveryAddress()) {
        isCheckingAvailability.value = false
        return
    }

    isCheckingAvailability.value = true
    try {
        await fetchDeliveryOptions()
    } catch (error) {
        console.error('Failed to check availability:', error)
        setPickup();
    } finally {
        isCheckingAvailability.value = false
    }
}

const checkAvailability = debounce(checkAvailabilityImpl, 500)

// --- watchers ---
watch(
    () => [form.address, form.suburb, form.state, form.postcode],
    () => {
        if (form.dispatch !== 'delivery') {
            return
        }

        if (!hasCompleteDeliveryAddress()) {
            availableDeliveryOptions.value = []
            deliveryOptionsValidatedAddressKey.value = ''
            setDeliveryOption(null)
            return
        }

        checkAvailability()
    }
)

watch(cartItems, (items) => {
    if (items.length && form.dispatch === 'delivery' && hasCompleteDeliveryAddress()) {
        checkAvailability()
    }
}, {deep: true})

watch(
    () => [form.address, form.suburb, form.state, form.postcode, form.dispatch],
    updateSessionStorage
)

watch(
    () => form.dispatch,
    async (dispatch) => {
        if (dispatch === 'delivery') {
            await waitForGoogleMaps()
            return
        }

        cleanupAddressAutocomplete()
    }
)

// Watch for changes in cart total to update delivery options
watch([itemsTotal, discountAmount], () => {
    if (form.dispatch === 'delivery' && hasCompleteDeliveryAddress()) {
        fetchDeliveryOptions()
    }
})

// Fetch delivery options with or without an address
async function fetchDeliveryOptions() {
    isFetchingDeliveryOptions.value = true
    try {
        let nextDeliveryOptions = []
        const requestAddressKey = hasCompleteDeliveryAddress() ? deliveryAddressKey() : ''

        // Prepare request data
        const requestData = {
            cart_total: itemsTotal.value,
            discount_total: discountAmount.value || 0,
        }

        // Add address details if available
        if (form.address && form.suburb && form.state && form.postcode) {
            requestData.address = form.address
            requestData.suburb = form.suburb
            requestData.state = form.state
            requestData.postcode = form.postcode

            // Add handles if we're checking for specific products
            if (cartItems.value.length) {
                requestData.handles = cartItems.value.map(i => i.handle || i.code)
            }
        }

        // Call the unified endpoint
        const { data } = await axios.post('/api/check-delivery', requestData)

        if (requestData.address && requestAddressKey !== deliveryAddressKey()) {
            return
        }

        // Update delivery settings - handle both boolean and string values
        deliveryEnabled.value = Boolean(data.delivery_enabled === true || data.delivery_enabled === 1 || data.delivery_enabled === "1")
        clickCollectEnabled.value = Boolean(data.click_collect_enabled === true || data.click_collect_enabled === 1 || data.click_collect_enabled === "1")
        syncDispatchWithAvailableMethods()

        // Only update store information if not already set from delivery config
        if (!storeName.value && data.storeName) {
            storeName.value = data.storeName
        }
        if (!storeAddress.value && data.storeAddress) {
            storeAddress.value = data.storeAddress
        }
        if (data.pickupNote !== undefined) {
            pickupNote.value = data.pickupNote
        }

        // Handle options based on whether we have an address or not
        if (requestData.address) {
            // We have an address, so we're checking availability
            if (data.results) {
                // Multiple products were checked
                availabilityResults.value = data.results

                // First, check if any product has delivery options
                let hasOptions = false
                Object.values(data.results).forEach(result => {
                    if (result.options && result.options.length > 0) {
                        hasOptions = true
                    }
                })

                if (hasOptions) {
                    // Find common options available for all products
                    const firstProductOptions = Object.values(data.results)[0]?.options || []
                    nextDeliveryOptions = firstProductOptions
                }

                // Build and save cache entries
                const updated = {}
                Object.entries(data.results).forEach(([h, r]) => {
                    updated[h] = {
                        address: form.address,
                        suburb: form.suburb,
                        state: form.state,
                        postcode: form.postcode,
                        available: r.available,
                        message: r.message,
                        timestamp: Date.now()
                    }
                })
                saveLookups(updated)
            } else if (data.options) {
                // Single product or no specific product was checked
                nextDeliveryOptions = data.options

                // Update the delivery status
                if (data.handle) {
                    // Save lookup for the specific product
                    const updated = {
                        [data.handle]: {
                            address: form.address,
                            suburb: form.suburb,
                            state: form.state,
                            postcode: form.postcode,
                            available: data.available,
                            message: data.message,
                            timestamp: Date.now()
                        }
                    }
                    saveLookups(updated)
                }
            }
        } else {
            // No address, just getting config/default options. Do not expose delivery
            // choices while checkout delivery still needs a complete address.
            if (form.dispatch !== 'delivery' && data.options && data.options.length > 0) {
                nextDeliveryOptions = data.options
            }
        }

        availableDeliveryOptions.value = nextDeliveryOptions
        deliveryOptionsValidatedAddressKey.value = requestData.address ? requestAddressKey : ''
        syncSelectedDeliveryOption(nextDeliveryOptions)

    } catch (error) {
        console.error('Failed to fetch delivery options:', error)

        if (clickCollectEnabled.value) {
            setPickup();
        }
    } finally {
        isFetchingDeliveryOptions.value = false
    }
}

// --- init on mount ---
onMounted(async () => {
    loadCart()

    // rehydrate form from sessionStorage if present
    try {
        const saved = JSON.parse(sessionStorage.getItem('ec_checkout_addr') || '{}')
        Object.assign(form, saved)
    } catch {
    }

    if (!hasCompleteDeliveryAddress() && selectedDeliveryOption.value?.id !== 'pickup') {
        setDeliveryOption(null)
    }

    // Fetch delivery config to get store information
    try {
        const config = await getDeliveryConfig();
        if (config) {
            // Update store information if available in the config
            if (config.storeName) {
                storeName.value = config.storeName;
            }
            if (config.storeAddress) {
                storeAddress.value = config.storeAddress;
            }
        }
    } catch (error) {
        console.error('Error fetching delivery config:', error);
    }

    // Fetch delivery options when first landing on the checkout page
    await fetchDeliveryOptions()

    if (form.dispatch === 'delivery') {
        await waitForGoogleMaps()
    }
})

onUnmounted(() => {
    stopWaitingForGoogleMaps()
    cleanupAddressAutocomplete()
})

</script>

<style scoped>
.is-invalid {
    border-color: #dc3545 !important;
}

.position-relative {
    position: relative;
}

.position-absolute {
    position: absolute;
    top: 0.25rem;
    right: 0.5rem;
    font-size: .75rem;
    color: #dc3545;
}

.invalid-feedback {
    width: auto;
    margin-top: 0;
}

.list-group-item.disabled {
    background-color: #f8f9fa;
    color: #6c757d;
    cursor: not-allowed;
}

.text-danger {
    color: #dc3545;
}

.small {
    font-size: 0.875em;
}

/* Delivery Options Styling */
.delivery-options {
    display: flex;
    flex-direction: column;
    gap: 10px;
    margin-bottom: 15px;
}

.delivery-option {
    border: 2px solid #e9ecef;
    border-radius: 8px;
    padding: 12px;
    cursor: pointer;
    transition: all 0.2s ease;
    margin-bottom: 0;
}

.delivery-option:hover {
    border-color: #ced4da;
    background-color: #f8f9fa;
}

.delivery-option.active {
    border-color: var(--config-color-primary);
    background-color: color-mix(in srgb, var(--config-color-primary) 5%, white);
}

.delivery-option.disabled {
    opacity: 1;
    cursor: not-allowed;
    background-color: #fff5f5;
    border-color: #dc3545;
    border-width: 2px;
}

.delivery-option.disabled .delivery-option-title {
    color: #dc3545;
    font-weight: 600;
}

.delivery-option-content {
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 100%;
}

.delivery-option-left {
    display: flex;
    align-items: center;
    gap: 10px;
}

.delivery-option-radio {
    margin: 0;
}

.delivery-option-details {
    display: flex;
    flex-direction: column;
}

.delivery-option-title {
    font-weight: 500;
}

.delivery-option-price {
    font-weight: 600;
    font-size: 1.1em;
    color: #212529;
}

/* Toggle styling */
.toggle-switch {
    position: relative;
    display: inline-flex;
    width: 100%;
    background: var(--config-color-primary);
    border-radius: 999px;
    padding: 5px;
    justify-content: space-between;
    align-items: center;
}
.toggle-switch input[type="radio"] { display: none; }
.toggle-switch label {
    z-index: 1;
    flex: 1;
    text-align: center;
    cursor: pointer;
    padding: 10px 0;
    color: white;
    font-weight: 500;
    transition: color 0.3s;
    user-select: none;
}
.toggle-switch label.disabled { opacity: 0.5; cursor: not-allowed; }
.toggle-slider {
    position: absolute;
    top: 5px;
    bottom: 5px;
    left: 5px;
    width: calc(50% - 10px);
    background: white;
    border-radius: 999px;
    transition: left 0.3s;
    z-index: 0;
}
#delivery:checked ~ .toggle-slider { left: calc(50% + 5px); }
#delivery:checked ~ label[for="delivery"],
#pickup:checked ~ label[for="pickup"] { color: var(--config-color-primary); }

.with-loader {
    position: relative;      /* establish containing block */
    overflow: hidden;        /* prevent spinner from expanding button */
}

/* Keep the text perfectly centered even when spinner shows */
.btn-label {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 100%;
}

/* spinner container sits on top */
.loader-overlay {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    pointer-events: none;    /* so button disabled state still works */
}

/* the spinner itself */
.loader {
    border: 3px solid rgba(0, 0, 0, 0.1);
    border-top: 3px solid #fff;
    border-radius: 50%;
    width: 1.75rem;
    height: 1.75rem;
    animation: spin 0.6s linear infinite;
}

/* arrow spacing */
.icon-arrow {
    margin-left: 0.5rem;
}

@keyframes spin {
    to { transform: rotate(360deg); }
}

/* Dispatch widget styling */
.dispatch-widget__output__heading {
    font-size: 1rem;
    margin-bottom: 10px;
    font-weight: 500;
}

/* Message box styling */
.message-box {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 15px;
    border: 1px solid #e9ecef;
    border-radius: 8px;
    background-color: #f8f9fa;
    margin-bottom: 15px;
}

.message-box__label {
    flex: 1;
}

.message-box__label h4 {
    margin-bottom: 5px;
    font-size: 1.1rem;
}

.message-box__label p {
    margin-bottom: 0;
    color: #6c757d;
}

.message-box__value {
    margin-left: 15px;
}

.pill {
    display: inline-block;
    padding: 5px 10px;
    border-radius: 999px;
    font-size: 0.8rem;
    font-weight: 500;
}

.pill-primary {
    background-color: var(--config-color-primary);
    color: white;
}

/* Pickup note styling */
.pickup-note {
    font-size: 0.75rem;
    margin-top: 0.75rem;
    color: var(--config-color-text, #666);
    line-height: 1.4;
}

</style>
